import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { entitiesApi, membersApi } from '../api/client';

interface FilterEntity { id: string; name: string; entity_type: string; }
interface FilterMember { id: string; first_name: string; last_name: string; }

interface FilterContextType {
  entityFilter: string;        // entity id or '' for all
  memberFilter: string;        // member id or '' for all
  setEntityFilter: (id: string) => void;
  setMemberFilter: (id: string) => void;
  clearFilters: () => void;
  entities: FilterEntity[];
  members: FilterMember[];
  activeEntityName: string;
  activeMemberName: string;
  isFiltered: boolean;
  refreshFilterOptions: () => Promise<void>;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export function FilterProvider({ children }: { children: React.ReactNode }) {
  const [entityFilter, setEntityFilterState] = useState(() => localStorage.getItem('osprey_filter_entity') || '');
  const [memberFilter, setMemberFilterState] = useState(() => localStorage.getItem('osprey_filter_member') || '');

  const setEntityFilter = (id: string) => {
    setEntityFilterState(id);
    if (id) localStorage.setItem('osprey_filter_entity', id);
    else localStorage.removeItem('osprey_filter_entity');
  };
  const setMemberFilter = (id: string) => {
    setMemberFilterState(id);
    if (id) localStorage.setItem('osprey_filter_member', id);
    else localStorage.removeItem('osprey_filter_member');
  };
  const [entities, setEntities] = useState<FilterEntity[]>([]);
  const [members, setMembers] = useState<FilterMember[]>([]);

  const refreshFilterOptions = useCallback(async () => {
    try {
      const [entRes, memRes] = await Promise.all([
        entitiesApi.list({ limit: 100 }),
        membersApi.list({ limit: 100 }),
      ]);
      setEntities((entRes.data.data || []).map((e: any) => ({
        id: e.id, name: e.name, entity_type: e.entity_type,
      })));
      setMembers((memRes.data.data || []).map((m: any) => ({
        id: m.id, first_name: m.first_name, last_name: m.last_name,
      })));
    } catch (_) {}
  }, []);

  useEffect(() => { refreshFilterOptions(); }, [refreshFilterOptions]);

  const clearFilters = () => {
    setEntityFilter('');
    setMemberFilter('');
    localStorage.removeItem('osprey_filter_entity');
    localStorage.removeItem('osprey_filter_member');
  };

  const activeEntityName = entityFilter
    ? entities.find(e => e.id === entityFilter)?.name || 'Unknown'
    : 'All Entities';
  const activeMemberName = memberFilter
    ? (() => { const m = members.find(m => m.id === memberFilter); return m ? `${m.first_name} ${m.last_name}` : 'Unknown'; })()
    : 'All People';

  const isFiltered = entityFilter !== '' || memberFilter !== '';

  return (
    <FilterContext.Provider value={{
      entityFilter, memberFilter, setEntityFilter, setMemberFilter,
      clearFilters, entities, members, activeEntityName, activeMemberName,
      isFiltered, refreshFilterOptions,
    }}>
      {children}
    </FilterContext.Provider>
  );
}

export function useFilter() {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilter must be used within FilterProvider');
  return ctx;
}
